import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Form } from 'antd';
import { snakeCase } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { FieldOptionBase } from 'api/tasks';
import { SelectMode } from 'components/atoms/select';
import { MultiSelectField, MultiUserField } from '../fields';
import { MultiSelectFieldFilter } from '../filters/multi-select';
import { MultiUserFieldFilter } from '../filters/multi-user';
import {
  isOptional,
  SelectFilterOperationType,
  SelectFilterOperationTypes,
  SelectFilterOperationTypesForSprintField,
} from '../filters/select';
import { Select } from './components';
import { EditorComponents } from './editor-components';
import { MultiSelectEditor } from './multi-select-editor';
import { CyWrapper } from './wrappers/cy-wrapper';

interface Props {
  filter: MultiSelectFieldFilter | MultiUserFieldFilter;
  options: FieldOptionBase[];
  components: EditorComponents;
  onChange: (filter: MultiSelectFieldFilter | MultiUserFieldFilter) => void;
  getFilterOperationTypeOptions?: (
    field: MultiSelectField | MultiUserField,
  ) => MultiSelectFieldFilter[] | MultiUserFieldFilter[];
  type?: SelectMode;
  getOperationOptionRenderer?: (
    type: SelectFilterOperationType,
  ) => null | JSX.Element;
}

const options = (
  field: MultiSelectField | MultiUserField,
): MultiSelectFieldFilter[] | MultiUserFieldFilter[] =>
  SelectFilterOperationTypes.filter(
    (x) => !field.metaData?.required || !isOptional(x),
  ).map(
    (type) =>
      ({
        field,
        type,
      } as any as MultiSelectFieldFilter),
  );

export const MultiSelectBaseCriteria: FC<Props> = ({
  filter,
  options: valueOptions,
  components,
  onChange,
  getFilterOperationTypeOptions = options,
  type = 'basic',
  getOperationOptionRenderer,
}) => {
  const getFilterOperationType = (field) => {
    if (field.fieldName === 'associatedSprints') {
      return SelectFilterOperationTypesForSprintField.map(
        (operationType) =>
          ({
            field,
            type: operationType,
          } as MultiSelectFieldFilter),
      );
    }

    return getFilterOperationTypeOptions(field);
  };

  return (
    <>
      <Form.Item>
        <Select
          disabled={filter == null}
          value={filter?.type}
          suffixIcon={<ChevronDown16 />}
          onChange={(selected) =>
            onChange({
              ...filter,
              type: selected,
            } as any)
          }
        >
          {(filter?.field == null
            ? []
            : (getFilterOperationType(filter.field) as (
                | MultiSelectFieldFilter
                | MultiUserFieldFilter
              )[])
          ).map((option) => (
            <Select.Option key={option.type} value={option.type}>
              <CyWrapper filter={filter} option={option}>
                {getOperationOptionRenderer?.(option.type) ?? (
                  <FormattedMessage
                    id={`task_list.filters.${snakeCase(
                      SelectFilterOperationType[option.type],
                    )}`}
                  />
                )}
              </CyWrapper>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {filter != null &&
        (filter.type === SelectFilterOperationType.Is ||
          filter.type === SelectFilterOperationType.IsNot ||
          filter.type === SelectFilterOperationType.IsOneOf ||
          filter.type === SelectFilterOperationType.IsNoneOf) && (
          <Form.Item>
            <MultiSelectEditor
              field={filter.field}
              filterType={filter.type}
              components={components}
              options={valueOptions}
              value={filter.value ?? []}
              onChange={(latest: number[]) =>
                onChange({
                  ...filter,
                  value: latest,
                })
              }
              type={type}
            />
          </Form.Item>
        )}
    </>
  );
};
